import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { ArrowUpDown, MapPin } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CompanyRow } from '@/hooks/useAccounts';
import { CategoryBadge } from './CategoryBadge';
import { AccountStatusBadge } from '@/components/targeting/StatusBadge';
import { Badge } from '@/components/ui/badge';

interface AccountsTableProps {
  data: CompanyRow[];
  totalCount: number;
  pageSize: number;
  pageIndex: number;
  onPageSizeChange: (size: number) => void;
  onPageIndexChange: (index: number) => void;
}

export function AccountsTable({
  data,
  totalCount,
  pageSize,
  pageIndex,
  onPageSizeChange,
  onPageIndexChange,
}: AccountsTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<CompanyRow>[]>(
    () => [
      {
        accessorKey: 'company_name',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Account
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div>
              <span className="font-medium text-foreground">
                {row.original.company_name}
                {row.original.parent_company_name && (
                  <span className="text-muted-foreground font-normal"> ({row.original.parent_company_name})</span>
                )}
              </span>
              {row.original.domain && (
                <div className="text-xs text-muted-foreground">{row.original.domain}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'primary_category',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Category
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <CategoryBadge category={row.original.primary_category} />,
      },
      {
        accessorKey: 'account_tier',
        header: 'Status',
        cell: ({ row }) => <AccountStatusBadge status={row.original.account_tier} />,
        enableSorting: false,
      },
      {
        accessorKey: 'carr_modality',
        header: 'Modality Type',
        cell: ({ row }) => {
          const mod = row.original.carr_modality;
          if (!mod || mod === 'Not Researched') return <span className="text-sm text-muted-foreground">—</span>;
          const color = mod === 'Allogeneic' ? 'bg-blue-100 text-blue-800 border-blue-200'
            : mod === 'Autologous' ? 'bg-green-100 text-green-800 border-green-200'
            : mod === 'Both' ? 'bg-purple-100 text-purple-800 border-purple-200'
            : 'bg-gray-100 text-gray-600 border-gray-200';
          return <Badge variant="outline" className={`text-xs ${color}`}>{mod}</Badge>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'icp_score',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            ICP Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const score = row.original.icp_score;
          if (score == null) return <span className="text-sm text-muted-foreground">—</span>;
          const tier = row.original.icp_tier;
          const color = tier === 'tier_1' ? 'text-green-700' : tier === 'tier_2' ? 'text-blue-700' : tier === 'tier_3' ? 'text-amber-700' : 'text-muted-foreground';
          return <span className={`text-sm font-medium ${color}`}>{score}</span>;
        },
      },
      {
        accessorKey: 'headquarters_country',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Location
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const { headquarters_city, headquarters_state, headquarters_country } = row.original;
          const isUS = headquarters_country === 'United States' || headquarters_country === 'US';
          const secondary = isUS ? headquarters_state : headquarters_country;
          const parts = [headquarters_city, secondary].filter(Boolean);
          if (!parts.length) return <span className="text-sm text-muted-foreground">—</span>;
          return (
            <span className="text-sm flex items-center gap-1">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              {parts.join(', ')}
            </span>
          );
        },
      },
      {
        accessorKey: 'modalities',
        header: 'Modality',
        cell: ({ row }) => {
          const mods = row.original.modalities;
          if (!mods?.length) return <span className="text-sm text-muted-foreground">—</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {mods.slice(0, 2).map((m) => (
                <Badge key={m} variant="outline" className="text-xs">{m}</Badge>
              ))}
              {mods.length > 2 && <span className="text-xs text-muted-foreground">+{mods.length - 2}</span>}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'clinical_phase',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Phase
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const phase = row.original.clinical_phase;
          if (!phase) return <span className="text-sm text-muted-foreground">—</span>;
          const label = phase.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          return <span className="text-sm">{label}</span>;
        },
      },
      {
        accessorKey: 'employee_range',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Size
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm">{row.original.employee_range || '—'}</span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-muted/50">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-xs">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => navigate(`/accounts/${row.original.id}`)}
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No accounts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Show</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              onPageSizeChange(Number(value));
              onPageIndexChange(0);
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span>of {totalCount.toLocaleString()} accounts</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageIndexChange(pageIndex - 1)}
            disabled={pageIndex === 0}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pageIndex + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageIndexChange(pageIndex + 1)}
            disabled={pageIndex >= totalPages - 1}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
